import { useMemo, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BatchesCreateTable from './BatchesCreateTable';
import moment from 'moment';
import useOrganizationStatus from '../../../hooks/useOrganisationStatus';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Flex } from '@chakra-ui/react';
import {
  selectCreateBatches,
  updateCreateBatches,
  mergeCreateBatches,
  resetCreatePageIndex,
  initialBatches,
} from '../../../store/redux/slices/batchList.slice';
import { useListBatchesQuery } from '../../../store/redux/api/batchApi';
import { useReportAndToastApiError } from '../../../hooks/useReportAndToastApiError';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

interface FilterEntity {
  uuid?: string;
}

interface BatchProgress {
  processed?: number;
  total?: number;
}

interface BatchItem {
  id: string;
  attributes: {
    name: string;
    passTemplateName: string;
    passTemplateId: string;
    status: string;
    progress?: BatchProgress;
    totalCount?: number;
    completedAt?: string | null;
    resultCsvUrl?: string | null;
  };
}

interface NormalizedBatchItem {
  id: string;
  name: string;
  passTemplate: string;
  passTemplateId: string;
  status: string;
  progress?: BatchProgress;
  totalCount?: number;
  completedAt: string | null;
  csvResults?: string | null;
}

interface Column {
  Header: string;
  accessor: string;
  style: {
    minWidth: string;
    width: string;
    textAlign?: string;
  };
}

interface BatchesCreateListProps {
  filterEntity?: FilterEntity | null;
  isActive?: boolean;
}

const BatchesCreateList = ({
  filterEntity,
  isActive = true,
}: BatchesCreateListProps) => {
  const { shouldRender } = useOrganizationStatus();
  const isReadOnly = useIsReadOnly();
  const dispatch = useDispatch();
  const batches = useSelector(selectCreateBatches);
  const reportAndToastApiError = useReportAndToastApiError();
  const fetchIdRef = useRef(0);
  const pollingCountRef = useRef(0);
  const isPollingRef = useRef(false);
  const MAX_POLLING_ATTEMPTS = 150; // 5 minutes at 2 second intervals

  const { items, total, pageIndex, pageSize, pageCount, loading, status } =
    batches;

  const columns = useMemo<Column[]>(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        style: {
          minWidth: '150px',
          width: '250px',
        },
      },
      {
        Header: 'Pass Template',
        accessor: 'passTemplate',
        style: {
          minWidth: '200px',
          width: '350px',
          textAlign: 'center',
        },
      },
      {
        Header: 'Completed At',
        accessor: 'completedAt',
        style: {
          minWidth: '150px',
          width: '200px',
        },
      },
      {
        Header: 'Count',
        accessor: 'totalCount',
        style: {
          minWidth: '40px',
          width: '90px',
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        style: {
          minWidth: '90px',
          width: '200px',
        },
      },
    ];
  }, []);

  const queryArgs = {
    entityId: filterEntity?.uuid,
    page: pageIndex + 1,
    perPage: pageSize,
    type: ['passCreation'] as string[],
  };

  const {
    data: apiResult,
    isFetching,
    isError,
    error,
    refetch,
  } = useListBatchesQuery(queryArgs, {
    skip: !shouldRender || !isActive,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    dispatch(updateCreateBatches(initialBatches()));
    dispatch(resetCreatePageIndex());
  }, [filterEntity, dispatch]);

  useEffect(() => {
    if (!shouldRender) return;

    const fetchId = ++fetchIdRef.current;

    if (isFetching) {
      if (status === 'idle') {
        dispatch(
          mergeCreateBatches({
            loading: true,
            status: 'pending',
          })
        );
      }
      return;
    }

    if (isError) {
      if (fetchId === fetchIdRef.current) {
        reportAndToastApiError(error);
        dispatch(
          mergeCreateBatches({
            items: [],
            total: 0,
            pageCount: 0,
            loading: false,
            status: 'rejected',
          })
        );
      }
      return;
    }

    if (apiResult && fetchId === fetchIdRef.current) {
      const batchesData = (apiResult?.data || []) as BatchItem[];
      const totalCount = apiResult?.meta?.totalCount ?? 0;
      const calculatedPageCount = Math.ceil(totalCount / pageSize);

      dispatch(
        mergeCreateBatches({
          items: batchesData,
          total: totalCount,
          pageCount: calculatedPageCount,
          loading: false,
          status: 'resolved',
        })
      );
    }
  }, [
    isFetching,
    isError,
    error,
    apiResult,
    pageSize,
    shouldRender,
    dispatch,
    reportAndToastApiError,
    status,
  ]);

  // Auto-refetch if any batches are processing or updating
  useEffect(() => {
    if (!apiResult?.data || !shouldRender || !isActive) {
      isPollingRef.current = false;
      return;
    }

    const hasProcessingBatches = apiResult.data.some(
      (item) =>
        item.attributes?.status === 'processing' ||
        item.attributes?.status === 'updating' ||
        item.attributes?.status === 'validating'
    );

    if (!hasProcessingBatches) {
      pollingCountRef.current = 0;
      isPollingRef.current = false;
      return;
    }

    if (pollingCountRef.current >= MAX_POLLING_ATTEMPTS) {
      isPollingRef.current = false;
      return;
    }

    isPollingRef.current = true;

    const intervalId = setInterval(() => {
      pollingCountRef.current += 1;
      if (pollingCountRef.current >= MAX_POLLING_ATTEMPTS) {
        clearInterval(intervalId);
        isPollingRef.current = false;
        return;
      }
      refetch();
    }, 2000);

    return () => {
      clearInterval(intervalId);
      isPollingRef.current = false;
    };
  }, [apiResult, refetch, shouldRender, isActive]);

  const fetchData = useCallback(
    ({ pageSize, pageIndex }: { pageSize: number; pageIndex: number }) => {
      dispatch(
        mergeCreateBatches({
          pageIndex,
          pageSize,
        })
      );
    },
    [dispatch]
  );

  const normalizedData = useMemo<NormalizedBatchItem[]>(() => {
    if (items.length > 0) {
      return (items as BatchItem[]).map((item) => ({
        id: item.id,
        name: item.attributes.name,
        passTemplate: item.attributes.passTemplateName,
        passTemplateId: item.attributes.passTemplateId,
        status: item.attributes.status,
        completedAt: item.attributes.completedAt
          ? moment(item.attributes.completedAt).format('MMM D, YYYY HH:mm')
          : null,
        progress: item.attributes.progress,
        totalCount: item.attributes.totalCount,
        csvResults: item.attributes.resultCsvUrl ?? null,
      }));
    }
    return [];
  }, [items]);

  return (
    <>
      {shouldRender && (
        <Flex justifyContent={'flex-end'} pb={4}>
          {isReadOnly ? (
            <LockedActionButton
              reason="issue-passes"
              mt="10px"
              mr={{ base: '0px', sm: '10px' }}
              px={{ base: '20px', sm: '40px' }}
              size="sm"
            >
              Create via CSV
            </LockedActionButton>
          ) : (
            <Button
              mt="10px"
              mr={{ base: '0px', sm: '10px' }}
              px={{ base: '20px', sm: '40px' }}
              aria-label="Create a bulk update batch"
              size="sm"
              as={RouterLink}
              to="/passes/batches/create"
            >
              Create via CSV
            </Button>
          )}
        </Flex>
      )}
      <BatchesCreateTable
        header="Bulk create"
        columns={columns}
        data={normalizedData}
        fetchData={fetchData}
        isLoading={loading || status === 'pending'}
        pageCount={pageCount}
        total={total}
        // filterEntity={filterEntity}
      />
    </>
  );
};

export default BatchesCreateList;
